import {
  Typeahead,
  useHint,
  type TypeaheadComponentProps,
  type TypeaheadRef,
} from 'react-bootstrap-typeahead';
import type {HintProps} from 'react-bootstrap-typeahead/types/components/Hint';
import type {
  InputProps,
  TypeaheadInputProps,
} from 'react-bootstrap-typeahead/types/types';
import cx from 'classnames';
import {forwardRef, useId} from 'react';

/**
 * This component and the code below are heavily inspired by:
 * https://github.com/ericgio/react-bootstrap-typeahead/blob/924627376275c78988718230c0438748ff673a6d
 *
 * The main difference is in how the hint is displayed and linked to the input,
 * making the component more accessible. In the original implementation, the hint
 * is an additional input element that is aria-hidden.
 */
const AccessibleTypeahead = forwardRef<TypeaheadRef, TypeaheadComponentProps>(
  ({...typeaheadProps}, ref) => {
    const autoGeneratedId = `typeahead-${useId()}`;
    const typeaheadId = typeaheadProps.id ?? autoGeneratedId;

    const renderInput = (inputProps: TypeaheadInputProps) => {
      const {isInvalid, isValid, size} = typeaheadProps;
      const commonProps = {
        ...inputProps,
        isInvalid,
        isValid,
        size,
      };
      return <AccessibleTypeaheadInput {...commonProps} />;
    };

    return (
      <Typeahead
        ref={ref}
        id={typeaheadId}
        {...typeaheadProps}
        renderInput={renderInput}
      />
    );
  },
);

const AccessibleTypeaheadInput = ({
  inputRef,
  referenceElementRef,
  ...props
}: TypeaheadInputProps) => {
  const autoGeneratedId = useId();
  const inputId = props.id ?? `typeahead-input-${autoGeneratedId}`;
  const hintId = `${inputId}-hint`;
  return (
    <AccessibleHint hintId={hintId}>
      <BaseInput
        {...propsWithBsClassName(props)}
        id={inputId}
        hintId={hintId}
        ref={node => {
          inputRef(node);
          referenceElementRef(node);
        }}
      />
    </AccessibleHint>
  );
};

const BaseInput = forwardRef<HTMLInputElement, InputProps & {hintId: string}>(
  (props, ref) => {
    const {hintText} = useHint();
    const originalDescribedBy = props?.['aria-describedby'] ?? '';
    const describedBy = [
      ...originalDescribedBy.split(' '),
      hintText ? props.hintId : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <input
        {...props}
        aria-describedby={describedBy}
        className={cx('rbt-input-main', props.className)}
        ref={ref}
      />
    );
  },
);

const AccessibleHint = ({
  children,
  className,
  hintId,
}: HintProps & {hintId: string}) => {
  const {hintRef, hintText} = useHint();
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flex: 1,
        height: '100%',
        position: 'relative',
      }}
    >
      {children}
      <span
        id={hintId}
        className="rbt-input-hint"
        ref={hintRef}
        style={{
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          boxShadow: 'none',
          color: 'rgba(0, 0, 0, 0.54)',
          left: 0,
          pointerEvents: 'none',
          position: 'absolute',
          top: 0,
          width: '100%',
        }}
        aria-live="polite"
      >
        {hintText}
      </span>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
const SIZES = ['lg', 'sm'] as const;
type Size = (typeof SIZES)[number];

interface Props {
  className?: string;
  isInvalid?: boolean;
  isValid?: boolean;
  size?: Size;
}

/**
 * Returns Bootstrap classnames from `size` and validation props, along
 * with pass-through props.
 */
const propsWithBsClassName = <T,>({
  className,
  isInvalid,
  isValid,
  size,
  ...props
}: Props & T) => {
  return {
    ...props,
    className: cx(
      'form-control',
      'rbt-input',
      size ? `form-control-${size}` : '',
      {
        'is-invalid': isInvalid,
        'is-valid': isValid,
      },
      className,
    ),
  };
};

export default AccessibleTypeahead;
